/**
 * The small decisions that have to be made the same way everywhere.
 *
 * Almost every false positive in this domain is a normalization disagreement.
 * One tool lower-cases a path and another does not, so `Stream/Police.YFT` and
 * `stream/police.yft` are two files. One strips `_hi` and another strips `+hi`,
 * so a high-detail model reads as an orphan. One trims a `<modelName>` and
 * another does not, so `police ` never matches `police`.
 *
 * None of that is interesting, and all of it is load-bearing, so it lives here
 * and nowhere else.
 */

// ─── Paths ───────────────────────────────────────────────────────────────────

/**
 * A path in the one form the rest of the layer compares.
 *
 * Backslashes become slashes because a Windows collector and a zip reader
 * disagree; a leading `./` goes because manifests write it and directory
 * listings do not; repeated slashes collapse because hand-written manifests have
 * them. Case is *not* touched — `pathKey` is for comparing, this is for showing.
 */
export function normalizePath(path: string): string {
  return path
    .replace(/\\/g, "/")
    .replace(/^\.\//, "")
    .replace(/\/{2,}/g, "/")
    .replace(/\/+$/, "");
}

/** The comparison key for a path: normalized and lower-cased. */
export function pathKey(path: string): string {
  return normalizePath(path).toLowerCase();
}

/** `stream/Police.YFT` → `Police.YFT`. */
export function baseName(path: string): string {
  const normalized = normalizePath(path);
  const slash = normalized.lastIndexOf("/");
  return slash === -1 ? normalized : normalized.slice(slash + 1);
}

/** `Police.YFT` → `yft`. No leading dot; `""` when there is no extension. */
export function extensionOf(nameOrPath: string): string {
  const name = baseName(nameOrPath);
  const dot = name.lastIndexOf(".");
  return dot <= 0 ? "" : name.slice(dot + 1).toLowerCase();
}

/** `Police.YFT` → `Police`. The name with its extension removed. */
export function stemOf(nameOrPath: string): string {
  const name = baseName(nameOrPath);
  const dot = name.lastIndexOf(".");
  return dot <= 0 ? name : name.slice(0, dot);
}

/** Whether any segment of the path is a folder called `stream`. */
export function isInStreamFolder(path: string): boolean {
  return pathKey(path).split("/").includes("stream");
}

// ─── Names ───────────────────────────────────────────────────────────────────

/** Trimmed and lower-cased, for every identifier comparison in the layer. */
export function normalizeName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * The two suffixes GTA uses for high-detail assets, and they are not the same.
 *
 * Models use `_hi`; texture dictionaries use `+hi`. That is not a typo in
 * anybody's code — `police_hi.yft` sits next to `police+hi.ytd` in Rockstar's own
 * files — and a tool that normalizes one and not the other reports half of every
 * correct vehicle as unreferenced.
 */
const HIGH_LOD_SUFFIX = /(_hi|\+hi)$/i;

export function isHighLodName(nameOrPath: string): boolean {
  return HIGH_LOD_SUFFIX.test(stemOf(nameOrPath));
}

/** `police_hi.yft` → `police.yft`. Names without a suffix come back unchanged. */
export function stripHighLod(nameOrPath: string): string {
  const stem = stemOf(nameOrPath);
  const extension = extensionOf(nameOrPath);
  const stripped = stem.replace(HIGH_LOD_SUFFIX, "");
  return extension === "" ? stripped : `${stripped}.${extension}`;
}

/** The high-detail companion names for a base asset, in the game's spelling. */
export function highLodNames(stem: string, extension: string): string[] {
  return extension.toLowerCase() === "ytd"
    ? [`${stem}+hi.${extension}`]
    : [`${stem}_hi.${extension}`];
}

/**
 * Whether a spawn name is one the game can actually use.
 *
 * The engine hashes the name and looks it up; a space or a punctuation mark does
 * not fail loudly, it produces a hash nothing matches, and the vehicle silently
 * does not exist. Rockstar's own names are `[a-z0-9_]` and nothing else, and
 * every working custom vehicle follows suit.
 */
const VALID_SPAWN_NAME = /^[a-z0-9_]+$/;

export function isValidSpawnName(name: string): boolean {
  return VALID_SPAWN_NAME.test(name.trim().toLowerCase());
}

/** The characters in a spawn name that the game cannot use, for the message. */
export function invalidSpawnNameChars(name: string): string[] {
  const bad = new Set<string>();
  for (const char of name.trim()) {
    if (!/[a-zA-Z0-9_]/.test(char)) bad.add(char);
  }
  return [...bad];
}

/**
 * `audioNameHash` is looked up the same way and has the same failure, but it is
 * conventionally upper-cased and may contain digits — the check is on the
 * character class only.
 */
export function isValidAudioName(name: string): boolean {
  return /^[A-Za-z0-9_]+$/.test(name.trim());
}

// ─── Fuzzy matching ──────────────────────────────────────────────────────────

/**
 * Levenshtein distance, capped so a hopeless pair costs nothing to reject.
 *
 * Two rows rather than a full matrix: the inputs are file and resource names, so
 * this runs thousands of times per report and never on anything long.
 */
export function editDistance(a: string, b: string, cap = Number.MAX_SAFE_INTEGER): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > cap) return cap + 1;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let previous = new Array<number>(b.length + 1);
  let current = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j += 1) previous[j] = j;

  for (let i = 1; i <= a.length; i += 1) {
    current[0] = i;
    let rowBest = current[0];
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(current[j - 1] + 1, previous[j] + 1, previous[j - 1] + cost);
      if (current[j] < rowBest) rowBest = current[j];
    }
    if (rowBest > cap) return cap + 1;
    const swap = previous;
    previous = current;
    current = swap;
  }
  return previous[b.length];
}

/** 1 for identical, 0 for nothing in common. Length-normalized edit distance. */
export function similarity(a: string, b: string): number {
  const longest = Math.max(a.length, b.length);
  if (longest === 0) return 1;
  return 1 - editDistance(a, b) / longest;
}

export type Suggestion = { value: string; confidence: number };

/**
 * The closest candidate to a name, or `null` if nothing is close enough.
 *
 * The floor is the whole point. "Did you mean `carcols.meta`?" is helpful when
 * the input was `carcol.meta` and actively misleading when it was `vehicles.meta`,
 * and a suggestion engine with no floor produces the second kind constantly.
 * 0.72 is set where a single-character slip in a short name still passes and two
 * unrelated GTA file names do not.
 */
export function closestMatch(
  needle: string,
  candidates: Iterable<string>,
  minConfidence = 0.72,
): Suggestion | null {
  const target = normalizeName(needle);
  let best: Suggestion | null = null;
  for (const candidate of candidates) {
    const score = similarity(target, normalizeName(candidate));
    if (score < minConfidence) continue;
    if (!best || score > best.confidence) best = { value: candidate, confidence: score };
  }
  return best;
}

/**
 * A path that exists but is spelled with different capitals.
 *
 * Windows does not care and Linux does, so this is the bug that only appears
 * after deployment: the manifest says `Stream/Police.yft`, the file is
 * `stream/police.yft`, it works on the author's machine and 404s on the server.
 */
export function caseOnlyMatch(path: string, known: Iterable<string>): string | null {
  const key = pathKey(path);
  for (const candidate of known) {
    if (candidate === path) return null;
    if (pathKey(candidate) === key) return candidate;
  }
  return null;
}

// ─── Text positions ──────────────────────────────────────────────────────────

/**
 * Line and column for a byte offset, both 1-based.
 *
 * Computed on demand rather than kept as an index: findings are rare relative to
 * the size of the files they are found in, and a line table for every parsed
 * file would cost more than the handful of lookups it saves.
 */
export function positionAt(source: string, offset: number): { line: number; column: number } {
  const clamped = Math.max(0, Math.min(offset, source.length));
  let line = 1;
  let lineStart = 0;
  for (let i = 0; i < clamped; i += 1) {
    if (source.charCodeAt(i) === 10) {
      line += 1;
      lineStart = i + 1;
    }
  }
  return { line, column: clamped - lineStart + 1 };
}
